/**
 * Call-logs admin view API service (V4.4 — voice roadmap; v2 filters +
 * per-call timeline: Prompt 1.13).
 *
 * Tenant is taken from the global `x-tenant-id` header (set by the axios
 * interceptor). Agent/date filters are passed as query params.
 *
 * Pure async functions — no React state, no hooks.
 */
import { get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type {
  SessionOutcomeKind,
  TicketRef,
} from '@features/observability/types';

export interface VoiceCallRecord {
  id: string;
  agentId: string;
  chatSessionId: string;
  /** Null for native phone calls (telephony, BE 1.1). */
  vapiCallId: string | null;
  direction: string;
  fromNumber: string | null;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
  recordingUrl: string | null;
  summary: string | null;
  endedReason: string | null;
  cost: number | null;
  transcript: string | null;
  createdAt: string;
  updatedAt: string;
  // Telephony (BE 1.1) — absent/null on legacy Vapi rows.
  /** `phone` = Orlenn number (Twilio); `voice` or null = web/Vapi voice. */
  channel?: string | null;
  engine?: string | null;
  disposition?: CallDisposition | null;
  toNumber?: string | null;
  provider?: string | null;
  state?: string | null;
  answeredAt?: string | null;
  telephonyCost?: number | string | null;
  engineCost?: number | string | null;
  llmCost?: number | string | null;
  /** Derived business outcome + filed tickets (A8). */
  outcome?: SessionOutcomeKind;
  escalated?: boolean;
  tickets?: TicketRef[];
}

/** How a phone call ended (BE call-state CALL_DISPOSITIONS). */
export type CallDisposition =
  | 'resolved_by_ai'
  | 'ticket_created'
  | 'transferred'
  | 'taken_over'
  | 'voicemail'
  | 'abandoned'
  | 'rejected'
  | 'failed'
  /** Conversed, but the request was left open (or the AI ended on silence / time limit). */
  | 'unresolved';

export type CallChannelFilter = 'voice' | 'phone';

export interface CallTimelineEvent {
  type: string;
  source: string;
  occurredAt: string;
  payload: Record<string, unknown>;
}

export interface CallTimelineParticipant {
  seq: number;
  role: string;
  endpointType: string;
  engine: string | null;
  state: string;
  joinedAt: string | null;
  leftAt: string | null;
  /** 3.5: agent name (AI legs) / destination label (human legs); never a number. */
  label?: string | null;
}

/** Event log + legs of one phone call (Prompt 1.13). */
export interface CallTimeline {
  callId: string;
  state: string | null;
  disposition: CallDisposition | null;
  events: CallTimelineEvent[];
  participants: CallTimelineParticipant[];
  /** 3.5: names of the agents on the call (agent switches). */
  agents?: Record<string, string>;
  /** Phone call recording (Prompt 2.6); the audio: `recordingLink`. */
  recording?: CallRecordingSummary;
  /** The caller's voicemail (Prompt 2.7); null/absent when none was left. */
  voicemail?: VoicemailSummary | null;
}

export type VoicemailStatus = 'stored' | 'absent' | 'failed' | 'expired' | 'deleted';
export type VoicemailTranscriptStatus = 'done' | 'unavailable' | 'failed';

/** Where the voicemail's ticket was filed (the agent's ticket tool, or locally). */
export interface VoicemailTicket {
  provider: 'hubspot' | 'jira' | 'local';
  /** HubSpot ticket id, Jira issue key, or the local ticket id. */
  externalId: string;
  url: string | null;
  bugReportId: string | null;
}

export interface VoicemailSummary {
  status: VoicemailStatus | null;
  available: boolean;
  durationSec: number | null;
  recordedAt: string | null;
  transcript: string | null;
  /** Null while the transcript is still being made. */
  transcriptStatus: VoicemailTranscriptStatus | null;
  escalationId: string | null;
  ticket: VoicemailTicket | null;
}

export type CallRecordingStatus =
  | 'requested'
  | 'recording'
  | 'stored'
  | 'absent'
  | 'failed'
  | 'expired'
  | 'deleted';

export interface CallRecordingSummary {
  status: CallRecordingStatus | null;
  available: boolean;
  durationSec: number | null;
}

export interface CallRecordingLink {
  /** Signed, valid 5 minutes. */
  url: string;
  expiresAt: string;
  contentType: string;
  durationSec: number | null;
}

export interface PaginatedVoiceCalls {
  items: VoiceCallRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface ListVoiceCallsQuery {
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
  /** `voice` also returns legacy rows with no channel. */
  channel?: CallChannelFilter | null;
  engine?: string | null;
  disposition?: CallDisposition | null;
  page?: number;
  limit?: number;
}

export const voiceCallsService = {
  list(query: ListVoiceCallsQuery = {}): Promise<PaginatedVoiceCalls> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.channel) params.channel = query.channel;
    if (query.engine) params.engine = query.engine;
    if (query.disposition) params.disposition = query.disposition;
    if (query.page) params.page = String(query.page);
    if (query.limit) params.limit = String(query.limit);
    return get<PaginatedVoiceCalls>(endpoints.voiceCalls.list, { params });
  },

  /** Phone calls only; 404 for web/Vapi calls or while telephony is off. */
  timeline(id: string): Promise<CallTimeline> {
    return get<CallTimeline>(endpoints.voiceCalls.timeline(id));
  },

  /** A 5-minute signed link (every request is audited). */
  recordingLink(id: string): Promise<CallRecordingLink> {
    return get<CallRecordingLink>(endpoints.voiceCalls.recording(id));
  },

  /** The caller's voicemail: a 5-minute signed link (audited, Prompt 2.7). */
  voicemailLink(id: string): Promise<CallRecordingLink> {
    return get<CallRecordingLink>(endpoints.voiceCalls.voicemail(id));
  },
} as const;
